import React, { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { Location, RouterProvider, createBrowserRouter, redirect } from 'react-router-dom';
import { QueryClientProvider } from 'react-query';
import { UserWorkspaceResponse } from '@goldcast/api/backend';
import { queryClient } from './queryClient';
import { getBaseUrlPath } from './utils';
import Loader from '@/components/atoms/Loader';
import SessionDetail from '@/Pages/SessionDetail';
import Sessions from '@/Pages/Sessions/Sessions';
import { initUser } from '@/stores/user';
import { initializeFeatureFlags } from '@/services/featureFlag';
import ErrorBoundary from '@/App/ErrorBoundary/ErrorBoundary';
import { useAppContext } from '@/context/AppContext/AppContext';
import Homepage from '@/Pages/Homepage/Homepage';
import BrandKit from '@/Pages/BrandKit/BrandKit';
import featureFlagStore from '@/stores/featureFlagStore';
import { loadOrgUsageSummary } from '@/stores/orgUsageSummary';
import EventBus from '@/libs/eventBus/eventBus';
import { CustomEvents } from '@/libs/eventBus/constants';
import useFreeTrialHook from '@/hooks/useFreeTrialHook';
import { POST_TYPES } from '@/Pages/PostsListPage/constants';
import useAnalytics from '@/hooks/useAnalytics';
import { getEnvConfig } from '@/constants';
import VideoImportPage from '@/Pages/VideoImportPage/VideoImportPage';
import VideoImportProcessingPage from '@/Pages/VideoImportPage/VideoImportProcessingPage';
import { initFirstTimeUserStore } from '@/stores/firstTimeUserStore';
import SavedSearch from '@/Pages/SavedSearch';
import PostsListPageWrapper from '@/Pages/PostsListPage/PostsListPageWrapper';
import SavedSearchPostsListPageWrapper from '@/Pages/SavedSearch/SavedSearchPostsListPageWrapper';
import { VideoImportContextProvider } from '@/Pages/VideoImportPage/VideoImportContext';
import RecordingsPage from '@/Pages/RecordingsPage/RecordingsPage';
import { usePendingEditsConfirm } from '@/hooks/usePendingEditsConfirm';
import NewClipListPage from '@/Pages/ClipsListPage/NewClipListPage';
import { GlobalRestrictionDialog } from '@/components/GlobalRestrictionDialog';
import AiSearchInternalPage from '@/Pages/AiSearchInternalPage/AiSearchInternalPage';
import NewContentModal from '@/components/organisms/NewContentModal/NewContentModal';
import { OPEN_NEW_CONTENT_MODAL_EVENT } from '@/components/organisms/NewContentModal/constants';

let previousLocation: Location;

export default function App(): JSX.Element {
  const [user, setUser] = useState<UserWorkspaceResponse | undefined>();
  const [loading, setLoading] = useState(true);
  const [isNewContentModalOpen, setIsNewContentModalOpen] = useState(false);
  const { logger, adminAppStore, adminAppRouter, theme } = useAppContext();

  const featureFlags = useSyncExternalStore(featureFlagStore.subscribe, featureFlagStore.getSnapshot);

  const { isUsageSummaryLoading } = useFreeTrialHook();
  const { trackRouteChange, trackOrgUsage } = useAnalytics();

  usePendingEditsConfirm();

  const router = useMemo(
    () =>
      createBrowserRouter(
        [
          {
            path: '/',
            ErrorBoundary,
            element: <Homepage />,
            children: [
              {
                path: '/',
                element: <Sessions />
              },
              {
                path: '/brand-kit',
                element: <BrandKit />
              },
              {
                path: '/import',
                element: (
                  <VideoImportContextProvider>
                    <VideoImportPage />
                  </VideoImportContextProvider>
                ),
                children: [
                  {
                    path: '/import/:eventId/:broadcastId',
                    element: <VideoImportProcessingPage />
                  }
                ]
              }
            ]
          },
          {
            path: '/:eventId/:broadcastId',
            element: <SessionDetail />,
            ErrorBoundary,
            children: [
              {
                path: '/:eventId/:broadcastId/recordings',
                element: <RecordingsPage />
              },
              {
                path: '/:eventId/:broadcastId/clips',
                element: <NewClipListPage />
              },
              {
                path: '/:eventId/:broadcastId/highlights',
                element: <NewClipListPage />
              },
              {
                path: '/:eventId/:broadcastId/social',
                element: <PostsListPageWrapper postType={POST_TYPES.SOCIAL} />
              },
              {
                path: '/:eventId/:broadcastId/blog',
                element: <PostsListPageWrapper postType={POST_TYPES.BLOG} />
              },
              {
                path: '/:eventId/:broadcastId/email',
                element: <PostsListPageWrapper postType={POST_TYPES.EMAIL} />
              },
              {
                path: '/:eventId/:broadcastId/takeaways',
                element: <PostsListPageWrapper postType={POST_TYPES.TAKEAWAYS} />
              },
              {
                path: '/:eventId/:broadcastId/custom',
                element: <PostsListPageWrapper postType={POST_TYPES.CUSTOM} />
              }
            ]
          },
          {
            path: '/ai-search-internal',
            element: <AiSearchInternalPage />
          },
          {
            path: '/search',
            element: <SavedSearch />,
            ErrorBoundary,
            children: [
              {
                path: '/search/:id/clips',
                element: <NewClipListPage />
              },
              {
                path: '/search/:id/social',
                element: <SavedSearchPostsListPageWrapper postType={POST_TYPES.SOCIAL} />
              },
              {
                path: '/search/:id/blog',
                element: <SavedSearchPostsListPageWrapper postType={POST_TYPES.BLOG} />
              }
            ]
          },
          {
            path: '/:eventId/:broadcastId/clips/:clipId',
            loader: ({ params }) => {
              return redirect(`/${params.eventId}/${params.broadcastId}/clips?activeClipId=${params.clipId}`);
            }
          },
          {
            path: '/:eventId/:broadcastId/highlights/:clipId',
            loader: ({ params }) => {
              return redirect(`/${params.eventId}/${params.broadcastId}/highlights?activeClipId=${params.clipId}`);
            }
          },
          {
            /** This is for manually handling any errors that come in code
             * For example, when clip has been deleted and we move to /:clipId
             * Or if eventId or broadcastId is invalid and we move to /:eventId/:broadcastId */
            path: '/error/:code',
            element: <ErrorBoundary />
          }
        ],
        {
          basename: getBaseUrlPath()
        }
      ),
    []
  );

  useEffect(() => {
    const unsubscribe = router.subscribe(({ location: toLocation }) => {
      trackRouteChange(previousLocation, toLocation);

      previousLocation = toLocation;
    });
    return unsubscribe;
  }, [router, trackRouteChange]);

  // The full-screen loader should only gate the INITIAL load. Once the app has rendered its routes
  // once, later toggles of these shared flags (e.g. another MF root re-fetching the org usage
  // summary) must NOT swap the router back out for the loader — that would unmount/remount the whole
  // route tree (a visible "reload"). Latch after the first completed load.
  const hasCompletedInitialLoad = useRef(false);
  const isContentAppLoading = useMemo(() => {
    if (hasCompletedInitialLoad.current) {
      return false;
    }

    if (loading) {
      return true;
    }

    if (user && !featureFlags.areFlagsLoaded) {
      return true;
    }

    if (isUsageSummaryLoading) {
      return true;
    }

    return false;
  }, [loading, user, featureFlags.areFlagsLoaded, isUsageSummaryLoading]);

  useEffect(() => {
    if (!isContentAppLoading) {
      hasCompletedInitialLoad.current = true;
    }
  }, [isContentAppLoading]);

  useEffect(() => {
    const eventListener = EventBus.on(CustomEvents.SignOut, triggerSignOut);
    return () => {
      EventBus.off(CustomEvents.SignOut, eventListener);
    };
  }, []);

  // In standalone mode (no admin-app shell), the NewContentModal island is never mounted by the shell,
  // so we own the OPEN_NEW_CONTENT_MODAL_EVENT listener here instead.
  // Guard on standalone: in non-standalone mode the shell's FederatedNewContentModal already registers
  // an identical listener, and registering a second one would open two stacked modals simultaneously.
  useEffect(() => {
    if (!user?.is_content_lab_standalone) {
      return;
    }
    const openHandler = () => setIsNewContentModalOpen(true);
    document.addEventListener(OPEN_NEW_CONTENT_MODAL_EVENT, openHandler);
    return () => document.removeEventListener(OPEN_NEW_CONTENT_MODAL_EVENT, openHandler);
  }, [user?.is_content_lab_standalone]);

  // The shell-level readiness popup (a separate MF island) can't navigate this React app via the Vue
  // router — a Vue push only calls history.pushState, which createBrowserRouter never observes. So the
  // popup dispatches these document events and we perform the navigation with the data router directly,
  // which works from any route the CL app is mounted on.
  useEffect(() => {
    const openEditorListener = EventBus.on(
      CustomEvents.OpenUploadInEditor,
      ({ projectId, contentId }: { projectId: string; contentId: string }) => {
        if (projectId && contentId) {
          router.navigate(`/${projectId}/${contentId}/recordings`);
        }
      }
    );
    const goHomeListener = EventBus.on(CustomEvents.NavigateToContentLabHome, () => {
      router.navigate('/');
    });
    return () => {
      EventBus.off(CustomEvents.OpenUploadInEditor, openEditorListener);
      EventBus.off(CustomEvents.NavigateToContentLabHome, goHomeListener);
    };
  }, [router]);

  function triggerSignOut() {
    adminAppStore.dispatch?.('user/signUserOut').then(() => {
      adminAppRouter.push({
        name: adminAppStore.user?.is_content_lab_standalone ? 'ContentLabLogin' : 'Login'
      });
    });
  }

  useEffect(() => {
    if (!window.configs) {
      return;
    }
    const buildNumber = getEnvConfig('CONTENT_LAB_BUILD_NUMBER');
    window.configs['CONTENT_LAB_BUILD_NUMBER'] = buildNumber;
    logger.log(`Content Studio Build Number: ${buildNumber}`);
    logger.log(`Player Config Hash: ${getEnvConfig('PLAYER_CONFIG_HASH')}`);
    logger.log(`Code commit Hash: ${getEnvConfig('CODE_COMMIT_HASH')}`);
  }, []);

  useEffect(() => {
    initFirstTimeUserStore(!!adminAppStore.currentOrg?.newbie_content_user);

    const initializeUser = async () => {
      try {
        const user = await initUser(adminAppStore);
        setUser(user);
        loadOrgUsageSummary(user.organization).then(() => trackOrgUsage());
        initializeFeatureFlags(user);
      } catch (e: any) {
        adminAppRouter.push({ name: 'Login' });
        logger.warn('Failed to initialize user', e?.message);
      } finally {
        setLoading(false);
      }
    };

    initializeUser();
  }, [adminAppStore]);

  return (
    <div className="content-lab-root" data-theme={theme} data-testid="theme-root">
      {isContentAppLoading ? (
        <div className="absolute inset-0 z-10 flex h-screen items-center justify-center">
          <Loader />
        </div>
      ) : (
        <QueryClientProvider client={queryClient}>
          <RouterProvider router={router} />
          {user?.is_content_lab_standalone && (
            <NewContentModal isOpen={isNewContentModalOpen} onClose={() => setIsNewContentModalOpen(false)} />
          )}
        </QueryClientProvider>
      )}
      <GlobalRestrictionDialog />
    </div>
  );
}
